import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { categories } from '@/mocks/categories';
import { getVideosByCategory } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryVideos = slug ? getVideosByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <SEOMeta title="分类未找到" description="抱歉，您访问的分类页面不存在。" />
        <Navbar />
        <main className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 pt-16">
          <div className="w-20 h-20 flex items-center justify-center mb-6">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">
            分类未找到
          </h1>
          <p className="text-sm text-foreground-500 mb-6">抱歉，您访问的分类页面不存在。</p>
          <Link
            to="/"
            className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOMeta
        title={`${category.name} - 星空传媒高清资源`}
        description={`星空传媒${category.name}专区，${category.description}。海量高清视频免费在线观看，持续更新，画质清晰流畅。`}
        keywords={`星空传媒${category.name},星空传媒在线免费观看,${category.name}高清资源,星空传媒免费视频`}
        canonicalUrl={`https://sportzup.github.io/category/${slug}`}
        ogTitle={`${category.name} - 星空传媒高清资源`}
        ogDescription={`星空传媒${category.name}专区，${category.description}。海量高清视频免费在线观看。`}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                  <i className={`${category.icon} text-lg`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {category.name}
                </h1>
              </div>
              <p className="text-sm text-foreground-500">{category.description}</p>
            </div>

            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-product-shop="">
                {categoryVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center mb-4">
                  <i className="ri-inbox-line text-4xl text-foreground-300"></i>
                </div>
                <p className="text-sm text-foreground-500">该分类暂无内容，敬请期待更多精彩视频上线。</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}